import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarCheck, Sparkles, Users } from 'lucide-react';
import PIELayout from './PIELayout';
import { pieApi, piePersona, pieCoachSession } from './pieApi';
import { pct } from './format';
import { Perf } from './PIEDisplayMode';
import './pie.css';
import logger from '../../lib/logger';
import PIESkeleton from './PIESkeleton';

/**
 * PIELandingPage — the entry hero. Centered hero (Seertech-aligned)
 * with split-color title, persona picker grid below.
 */
export default function PIELandingPage() {
  const navigate = useNavigate();

  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    pieApi
      .listPersonas()
      .then((d) => setPersonas(d.personas || []))
      .catch((e) => {
        logger.error('listPersonas failed', e);
        setErr('Could not load personas. Has the backend been seeded?');
      })
      .finally(() => setLoading(false));
  }, []);

  const onPick = (id) => {
    piePersona.set(id);
    pieCoachSession.clear();
    navigate('/mirror/dashboard');
  };

  return (
    <PIELayout>
      <div className="pie-fade-in" data-testid="pie-landing">
        <section className="pie-hero-center">
          <span className="pie-eyebrow">
            <CalendarCheck size={12} /> Seertech Mirror
          </span>
          <h1 className="pie-hero-h1">
            Turn your day into <span className="pie-accent-text">measurable impact</span>
          </h1>
          <p className="pie-hero-sub">
            See where you stand today. Model the one move that would actually shift
            the needle. Validate it after — with the same engine your CFO trusts.
          </p>
        </section>

        <section data-testid="pie-persona-section">
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 18 }}>
            <h2 style={{ fontSize: 18, fontWeight: 600, color: 'var(--pie-text)' }}>
              Choose a persona to explore
            </h2>
            <span className="pie-chip">
              <Sparkles size={11} /> 5 archetypes · synthetic
            </span>
          </div>

          {loading && (
            <div className="pie-persona-grid" data-testid="pie-persona-grid-skeleton">
              {Array.from({ length: 8 }, (_, i) => (
                <PIESkeleton key={i} variant="card" height={188} />
              ))}
            </div>
          )}
          {err && <div style={{ color: 'var(--pie-danger)', fontSize: 14 }}>{err}</div>}

          {!loading && !err && (
            <>
              {personas.some((p) => p.is_demo_team_sofia) && (
                <DemoTeamGroup
                  members={personas.filter((p) => p.is_demo_team_sofia)}
                  onPick={onPick}
                />
              )}
              <div className="pie-persona-grid" data-testid="pie-persona-grid">
                {personas.filter((p) => !p.is_demo_team_sofia).map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`pie-persona-card ${p.is_manager ? 'pie-persona-card-manager' : ''}`}
                  onClick={() => onPick(p.id)}
                  data-testid={`pie-persona-${p.archetype}${p.is_manager ? '-manager' : ''}-${p.id}`}
                >
                  <div className="pie-persona-card-head">
                    <span className="pie-persona-archetype">{p.archetype_label}</span>
                    {p.is_manager && (
                      <span className="pie-persona-manager-badge" title="Has direct reports — opens the team view">
                        <Users size={10} aria-hidden /> Manager
                      </span>
                    )}
                  </div>
                  <span className="pie-persona-name">{p.name}</span>
                  <span className="pie-persona-role">{p.role_label}</span>
                  <div
                    style={{
                      display: 'flex',
                      gap: 14,
                      marginTop: 14,
                      paddingTop: 16,
                      borderTop: '1px solid var(--pie-border)',
                    }}
                  >
                    <Stat label="Capability" value={p.capability_score} />
                    <Stat label="Motivation" value={p.motivation_score} />
                    <Stat label="PE" value={p.personal_effectiveness} highlight />
                  </div>
                </button>
              ))}
              </div>
            </>
          )}
        </section>
      </div>
    </PIELayout>
  );
}

function Stat({ label, value, highlight = false }) {
  return (
    <div style={{ flex: 1 }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--pie-text-muted)',
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        className="pie-mono"
        style={{
          fontSize: 16,
          fontWeight: 700,
          color: highlight ? 'var(--pie-accent)' : 'var(--pie-text)',
          lineHeight: 1.2,
          wordBreak: 'break-word',
        }}
      >
        {value !== undefined ? (
          <Perf
            value={value}
            kind="pct01"
            style={{ fontSize: 'clamp(11px, 1.4vw, 14px)', whiteSpace: 'nowrap' }}
          />
        ) : (
          '—'
        )}
      </div>
    </div>
  );
}

/**
 * DemoTeamGroup — separate, eyebrow-labelled section above the
 * archetype grid showing the hand-picked Sofia + Ben + Saoirse team.
 * Same card shape as the regular grid so the visuals stay consistent;
 * what differs is the framing (a real reporting line, with the
 * manager flagged) so a demo viewer immediately sees "this is one
 * team I can navigate". Members come pre-sorted by the API
 * (manager → senior report → junior report).
 */
function DemoTeamGroup({ members, onPick }) {
  if (!members || members.length === 0) return null;
  const label = members[0]?.demo_team_label || "Sofia's team";
  return (
    <section
      data-testid="pie-demo-team-group"
      style={{
        marginBottom: 28,
        paddingLeft: 18,
        borderLeft: '3px solid var(--pie-accent)',
      }}
    >
      <div
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
          letterSpacing: '0.08em', color: 'var(--pie-accent)',
          marginBottom: 14,
        }}
      >
        <Users size={11} aria-hidden /> {label} <span style={{ color: 'var(--pie-text-muted)', fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>· hand-picked demo team</span>
      </div>
      <div className="pie-persona-grid">
        {members.map((p) => (
          <button
            key={p.id}
            type="button"
            className={`pie-persona-card ${p.is_manager ? 'pie-persona-card-manager' : ''}`}
            onClick={() => onPick(p.id)}
            data-testid={`pie-persona-demo-${p.id}`}
          >
            <div className="pie-persona-card-head">
              <span className="pie-persona-archetype">{p.archetype_label}</span>
              {p.is_manager && (
                <span className="pie-persona-manager-badge" title="Manager of this demo team">
                  <Users size={10} aria-hidden /> Manager
                </span>
              )}
            </div>
            <span className="pie-persona-name">{p.name}</span>
            <span className="pie-persona-role">{p.role_label}</span>
            <div
              style={{
                display: 'flex', gap: 14, marginTop: 14, paddingTop: 16,
                borderTop: '1px solid var(--pie-border)',
              }}
            >
              <Stat label="Capability" value={p.capability_score} />
              <Stat label="Motivation" value={p.motivation_score} />
              <Stat label="PE" value={p.personal_effectiveness} highlight />
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}
